import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  runTransaction
} from "firebase/firestore";
   

import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import "./InvoicePPD.css";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { serverTimestamp, increment } from "firebase/firestore";

const SignaturePadField = ({ label, value, onChange }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!value) return;

    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const scale = Math.min(
        canvas.width / img.width,
        canvas.height / img.height
      );

      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const x = (canvas.width - drawWidth) / 2;
      const y = (canvas.height - drawHeight) / 2;

      ctx.drawImage(img, x, y, drawWidth, drawHeight);
    };
    img.src = value;
  }, [value]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPoint(e);

    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);

    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPoint(e);

    ctx.lineTo(x, y);
    ctx.stroke();
    onChange(canvas.toDataURL("image/png"));
  };

  const stopDrawing = () => {
    setDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div style={{ marginBottom: "16px" }}>
      <label>{label}:</label>
      <canvas
        ref={canvasRef}
        width={320}
        height={120}
        style={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "320px",
          background: "#fff",
          display: "block",
          marginTop: "6px",
          touchAction: "none",
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <button
        type="button"
        className="red-button"
        style={{ marginTop: "8px" }}
        onClick={clearSignature}
      >
        Clear Signature
      </button>
    </div>
  );
};

const CompactMultiSelect = ({
  label = "",
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleValue = (opt) => {
    const text = opt.title ?? opt.label ?? opt.value ?? "";
    const exists = selectedValues.includes(text);

    const updated = exists
      ? selectedValues.filter((item) => item !== text)
      : [...selectedValues, text];

    onChange(updated, opt, exists ? "remove" : "add");
  };

  return (
    <div className="compact-multiselect" ref={wrapperRef}>
      <button
        type="button"
        className="compact-multiselect-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="compact-multiselect-text">
          {selectedValues.length
            ? `${selectedValues.length} selected`
            : placeholder}
        </span>
        <span className="compact-multiselect-arrow">{open ? "▲" : "▼"}</span>
      </button>

      {selectedValues.length > 0 && (
        <div className="compact-multiselect-tags">
          {selectedValues.map((value) => (
            <span key={value} className="compact-tag">
              {value}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="compact-multiselect-menu">
          {options.map((opt, i) => {
            const text = opt.title ?? opt.label ?? opt.value ?? "";
            const checked = selectedValues.includes(text);

            return (
              <label key={i} className="compact-multiselect-option">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleValue(opt)}
                />
                <span>{text}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PPDSentInvoices = () => {
  const logoOptions = [
    {
      name: "Priority Pest Defense",
      url: "https://firebasestorage.googleapis.com/v0/b/priority-backend.firebasestorage.app/o/logo-images%2Fprioritypestlogogreyblue.png?alt=media&token=da273ab0-3054-4675-bb96-3a3a4a242434",
      alignment: "center"
    }
  ];

  const PPD_NAME = "Priority Pest Defense";
  const staticTermsOptions = ["NET 30", "NET 15", "UPON INVOICE", "NONE"];

  const staticServiceDescriptions = [
    "Weekly Pest Control Service",
    "Semi-Monthly Pest Control Service",
    "Monthly Pest Control Service",
    "Quarterly Pest Control Service",
    "Annual Pest Control Service",
    "One-Time Pest Control Service",
    "Weekly Rodent Control Service",
    "Monthly Rodent Control Service",
    "Quarterly Rodent Control Service",
    "Annual Rodent Control Service",
    "One-Time Rodent Control Service",
    "Weekly Termite Service",
    "Monthly Termite Service",
    "Quarterly Termite Service",
    "Annual Termite Service",
    "One-Time Termite Service",
    "WDI Inspection",
    "Wildlife Inspection",
    "Weekly Wildlife Service",
    "Monthly Wildlife Service",
    "Quarterly Wildlife Service",
    "Annual Wildlife Service",
    "One-Time Wildlife Service",
    "Bed Bug Inspection",
    "Weekly Bed Bug Service",
    "Monthly Bed Bug Service",
    "Quarterly Bed Bug Service",
    "Annual Bed Bug Service",
    "One-Time Bed Bug Service",
  ];

  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
   const [newInvoice, setnewInvoice] = useState({
    logo: logoOptions[0]?.url || "",
    invoiceNumber: "",
    companyName: "",
    companyAddress: "",
    companyCityStateZip: "",
    siteName: "",
    management: "",
    managerName: "",
    address: "",
    cityStateZip: "",
    dropdownValues: {},
dropdownSelections: {},
dropdown: "",
chemicalUsed: "",
chemicalProducts: [],
pestTreated: "",
areasTreated: "",
windSpeed: "",
equipmentUsed: "",
weather: "",
temperature: "",
windDirection: "",
    timeIn: "",
    timeOut: "",
    dateOfService: "",
    purchaseOrder: "None",
    serviceDescription: "",
    quantity: "1.00",
    amount: "",
    serviceRows: [
      {
        serviceDescription: "",
        quantity: "1.00",
        amount: "",
      },
    ],
    jobDescription: "",
    price: "",
    submittedDate: "",
    submittedBy: "",
    images: [],
    terms: "NET 30",
    technicianSignature: "",
    customerSignature: "",
    customerUnavailableToSign: false,
  });
  const [proposalID, setProposalID] = useState(3000);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(""); // Add state for PDF download URL
  const navigate = useNavigate();
  const [termsOptions, setTermsOptions] = useState([]);
 

  const goToPPDApprovedInvoices = () => {
    navigate("/PPDApprovedInvoices");
  };
 
  const goToPPDCompletedInvoices = () => {
    navigate("/PPDCompletedInvoices");
  };

  const goToPPDSentInvoices = () => {
    navigate("/PPDSentInvoices");
  };

  const goToInvoicesPPD = () => {
    navigate("/InvoicesPPD");
  };

  const [sortOrder, setSortOrder] = useState("asc");


const [invoiceDropdowns, setInvoiceDropdowns] = useState([]);
const [activeSubOptions, setActiveSubOptions] = useState(null);

const getOptionDisplayText = (opt = {}) => {
  return opt.title ?? opt.label ?? opt.value ?? "";
};

const buildDropdownText = (items = [], label = "", includeLabel = true) => {
  if (!items.length) return "";
  return includeLabel ? `${label}:\n${items.join("\n")}` : items.join("\n");
};

const buildChemicalUsedDisplay = (chemicalProducts = []) => {
  return chemicalProducts
    .map((item) => {
      const chemical = item.chemical || "";
      const qty = item.qty || "";
      return qty ? `${chemical} - ${qty}` : chemical;
    })
    .join("\n");
};

const upsertChemicalProduct = (chemicalName, qty) => {
  setnewInvoice((prev) => {
    const current = Array.isArray(prev.chemicalProducts)
      ? [...prev.chemicalProducts]
      : [];

    const existingIndex = current.findIndex(
      (item) => item.chemical === chemicalName
    );

    if (existingIndex >= 0) {
      current[existingIndex] = {
        ...current[existingIndex],
        qty,
      };
    } else {
      current.push({
        chemical: chemicalName,
        qty,
      });
    }

    return {
      ...prev,
      chemicalProducts: current,
      chemicalUsed: buildChemicalUsedDisplay(current),
      dropdownSelections: {
        ...(prev.dropdownSelections || {}),
        chemicalUsed: current.map((item) => item.chemical),
      },
    };
  });
};

const removeChemicalProduct = (chemicalName) => {
  setnewInvoice((prev) => {
    const current = (prev.chemicalProducts || []).filter(
      (item) => item.chemical !== chemicalName
    );

    return {
      ...prev,
      chemicalProducts: current,
      chemicalUsed: buildChemicalUsedDisplay(current),
      dropdownSelections: {
        ...(prev.dropdownSelections || {}),
        chemicalUsed: current.map((item) => item.chemical),
      },
    };
  });
};

const promptForChemicalQty = (chemicalName, existingQty = "") => {
  const qty = window.prompt(
    `Enter product quantity for "${chemicalName}"`,
    existingQty || ""
  );

  if (qty === null) return null; // user cancelled
  return qty.trim();
};

const handleDropdownToggle = (field, optionObj, label) => {
  const clickedValue = getOptionDisplayText(optionObj);

  if (field === "chemicalUsed") {
    const existingChemical = (newInvoice.chemicalProducts || []).find(
      (item) => item.chemical === clickedValue
    );

    if (existingChemical) {
      removeChemicalProduct(clickedValue);
      return;
    }

    const qty = promptForChemicalQty(clickedValue, "");
    if (qty === null) return;

    upsertChemicalProduct(clickedValue, qty);
    return;
  }

  setnewInvoice((prev) => {
    const currentSelections = prev.dropdownSelections?.[field] || [];

    const selectedValues = currentSelections.includes(clickedValue)
      ? currentSelections.filter((item) => item !== clickedValue)
      : [...currentSelections, clickedValue];

    const updatedSelections = {
      ...(prev.dropdownSelections || {}),
      [field]: selectedValues,
    };

    const updatedDropdownValues = {
      ...(prev.dropdownValues || {}),
      [field]:
        field === "dropdown"
          ? selectedValues.join("\n")
          : buildDropdownText(selectedValues, label, true),
    };

    const mainDropdownText = updatedSelections.dropdown?.join("\n") || "";

    return {
      ...prev,
      dropdownSelections: updatedSelections,
      [field]: selectedValues.join("\n"),
      dropdownValues: updatedDropdownValues,
      jobDescription: mainDropdownText,
    };
  });
};

useEffect(() => {
  const fetchTerms = async () => {
    const snap = await getDocs(collection(db, "invoiceTerms"));
    setTermsOptions(snap.docs.map(d => d.data()));
  };
  fetchTerms();
}, []);

  // Helpers

  const calculateTotal = (quantity, amount) => {
    const qty = parseFloat(quantity);
    const amt = parseFloat(amount);
 
    if (Number.isNaN(qty) || Number.isNaN(amt)) return "";
    return (qty * amt).toFixed(2);
  };

  const formatInitials = (nameOrFirstLast) => {
    if (!nameOrFirstLast) return "";
    // Accept either "First Last" or already-initials and normalize to "F L"
    const parts = String(nameOrFirstLast).trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last  = parts[1]?.[0] || "";
    return [first, last].filter(Boolean).join(" ");
  };
 
  const formatDateMMDDYYYY = (isoOrYmd) => {
    if (!isoOrYmd) return "";
    // Handles "YYYY-MM-DD" from <input type="date"> or full ISO strings
    const d = new Date(isoOrYmd);
    if (Number.isNaN(d.getTime())) return isoOrYmd; // fallback
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const [selectedLogo, setSelectedLogo] = useState(logoOptions[0]);

  const handleLogoChange = (e) => {
    const selected = logoOptions.find(option => option.name === e.target.value);
    setSelectedLogo(selected);
 
    setnewInvoice(prev => ({
      ...prev,
      logo: selected?.url || ""
    }));
  };

 
  useEffect(() => {
    const fetchInvoices = async () => {
      const invoicesRef = collection(db, "invoices");
      const querySnapshot = await getDocs(invoicesRef);
   
      const invoicesData = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter((invoice) => {
        const status = (invoice.status || invoice.invoiceStatus || "").toLowerCase().trim();
      
        return (
          status === "sent" ||
          status === "invoice sent" ||
          invoice.sent === true
        );
      });
    
    setInvoices(invoicesData);
    };
   
    fetchInvoices();
   
    const fetchInvoiceDropdowns = async () => {
      const snap = await getDocs(collection(db, "invoiceDropdowns"));
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.order - b.order);
      setInvoiceDropdowns(data);
    };
   
    fetchInvoiceDropdowns();

    const fetchProperties = async () => {
      const propertiesRef = collection(db, "properties");
      const querySnapshot = await getDocs(propertiesRef);
      const propertiesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProperties(propertiesData);
    };

    const fetchUsers = async () => {
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    };

    const fetchCompanies = async () => {
      const companiesRef = collection(db, "companies");
      const querySnapshot = await getDocs(companiesRef);
      const companiesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCompanies(companiesData);
    };

    fetchInvoices();
    fetchProperties();
    fetchUsers();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedInvoice) {
      setnewInvoice({
        logo: selectedInvoice.logo || logoOptions[0]?.url || "",
        companyName: selectedInvoice.companyName || "",
        companyAddress: selectedInvoice.companyAddress || "",
        companyCityStateZip: selectedInvoice.companyCityStateZip || "",
        siteName: selectedInvoice.siteName || "",
        management: selectedInvoice.management || "",
        managerName: selectedInvoice.managerName || "",
        address: selectedInvoice.address || "",
        cityStateZip: selectedInvoice.cityStateZip || "",
        dropdownValues: selectedInvoice.dropdownValues || {},
        dropdownSelections: selectedInvoice.dropdownSelections || {},
        dropdown: selectedInvoice.dropdown || "",
        chemicalUsed: selectedInvoice.chemicalUsed || "",
chemicalProducts: selectedInvoice.chemicalProducts || [],
        pestTreated: selectedInvoice.pestTreated || "",
        areasTreated: selectedInvoice.areasTreated || "",
        windSpeed: selectedInvoice.windSpeed || "",
        equipmentUsed: selectedInvoice.equipmentUsed || "",
        weather: selectedInvoice.weather || "",
        temperature: selectedInvoice.temperature || "",
        windDirection: selectedInvoice.windDirection || "",
        timeIn: selectedInvoice.timeIn || "",
        dateOfService: selectedInvoice.dateOfService || "",
        jobDescription: selectedInvoice.jobDescription || "",
        price: selectedInvoice.price || "",
        submittedDate: selectedInvoice.submittedDate || "",
        submittedBy: selectedInvoice.submittedBy || "",
        images: selectedInvoice.images || [],
        terms: selectedInvoice.terms || "",
        invoiceNumber: selectedInvoice?.invoiceNumber || "",
timeOut: selectedInvoice?.timeOut || "",
purchaseOrder: selectedInvoice?.purchaseOrder || "None",
serviceDescription: selectedInvoice?.serviceDescription || "",
quantity: selectedInvoice?.quantity || "1.00",
amount: selectedInvoice?.amount || "",
serviceRows: getServiceRows(selectedInvoice),
technicianSignature: selectedInvoice?.technicianSignature || "",
customerSignature: selectedInvoice?.customerSignature || "",
customerUnavailableToSign: selectedInvoice?.customerUnavailableToSign || false,
      });
    }
  }, [selectedInvoice]);

  const sortedProperties = [...properties].sort((a, b) =>
    (a.propertyName || "").localeCompare(b.propertyName || "")
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setnewInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyNameChange = (e) => {
    const selectedCompany = companies.find(company => company.companyName === e.target.value);
    if (!selectedCompany) return;
 
    const matchedLogo = logoOptions.find(logo => logo.name === selectedCompany.companyName);
 
    setnewInvoice((prev) => ({
      ...prev,
      companyName: selectedCompany.companyName,
      companyAddress: selectedCompany.companyAddress || "",
      companyCityStateZip: `${selectedCompany.companyCity || ""}, ${selectedCompany.companyState || ""} ${selectedCompany.companyZip || ""}`.trim(),
      logo: matchedLogo?.url || logoOptions[0]?.url || ""
    }));
 
    if (matchedLogo) {
      setSelectedLogo(matchedLogo);
    }
  };

  const handleSiteNameChange = (e) => {
    const selectedProperty = properties.find(property => property.propertyName === e.target.value);
    setnewInvoice((prev) => ({
      ...prev,
      siteName: selectedProperty.propertyName,
      management: selectedProperty.management || "",
      managerName: selectedProperty.managerName || "",
      address: selectedProperty.address || selectedProperty.managementAddress || "",
      cityStateZip: `${selectedProperty.city || selectedProperty.managementCity}, ${selectedProperty.state || selectedProperty.managementState} ${selectedProperty.zip || selectedProperty.managementZip}` || ""
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setnewInvoice(prevState => ({
      ...prevState,
      images: [...prevState.images, ...selectedFiles], // Append new files without removing URLs
    }));
  };
 
  const getNextInspectionInvoiceNumber = async () => {
    const counterRef = doc(db, "appCounters", "ppdServiceInspectionReport");

    const nextNumber = await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef);

      if (!counterSnap.exists()) {
        // first invoice generated should be 03222026
        transaction.set(counterRef, { lastNumber: 3222026 });
        return 3222026;
      }

      const lastNumber = counterSnap.data().lastNumber || 3222025;
      const newNumber = lastNumber + 1;

      transaction.update(counterRef, { lastNumber: newNumber });
      return newNumber;
    });

    return String(nextNumber).padStart(8, "0");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { images, ...invoiceData } = newInvoice;
 
      const invoiceDocRef = await addDoc(collection(db, "invoices"), {
        ...invoiceData,
        status: "Draft",
        sent: false,
        paid: false,
        createdAt: serverTimestamp(),
      });
 
      let imageURLs = [];
 
      if (images.length > 0) {
        const storage = getStorage();
        const uploadPromises = images.map(async (file) => {
          const storageRef = ref(storage, `invoices/${invoiceDocRef.id}/${file.name}`);          
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
 
        imageURLs = await Promise.all(uploadPromises);
        await updateDoc(invoiceDocRef, { imageURLs });
      }
 
      setInvoices((prev) => [
        ...prev,
        { id: invoiceDocRef.id, ...invoiceData, imageURLs },
      ]);
 
      setnewInvoice({
        logo: logoOptions[0]?.url || "",
        companyName: "",
        companyAddress: "",
        companyCityStateZip: "",
        siteName: "",
        management: "",
        managerName: "",
        address: "",
        cityStateZip: "",
        dropdownValues: {},
        dropdownSelections: {},
        dropdown: "",
        chemicalUsed: "",
        chemicalProducts: [],
        pestTreated: "",
        areasTreated: "",
        windSpeed: "",
        equipmentUsed: "",
        weather: "",
        temperature: "",
        windDirection: "",
        timeIn: "",
        dateOfService: "",
        jobDescription: "",
        price: "",
        submittedDate: "",
        submittedBy: "",
        images: [],
        terms: "",
        invoiceNumber: "",
timeOut: "",
purchaseOrder: "None",
serviceDescription: "",
quantity: "1.00",
amount: "",
serviceRows: [
  {
    serviceDescription: "",
    quantity: "1.00",
    amount: "",
  },
],
technicianSignature: "",
customerSignature: "",
customerUnavailableToSign: false,
      });
 
      setIsEditing(false);
    } catch (error) {
      console.error("Error adding invoice:", error);
    }
  };
 

  const handleEditProposal = async () => {
    try {
      const invoiceRef = doc(db, "invoices", selectedInvoice.id);
 
      // Extract files from newInvoice.images
      const files = newInvoice.images.filter(img => img instanceof File);
      let imageURLs = selectedInvoice.imageURLs || []; // Keep existing URLs
 
      if (files.length > 0) {
        const storage = getStorage();
        const uploadPromises = files.map(async (file) => {
          const storageRef = ref(
            storage,
            `invoices/${selectedInvoice.id}/${file.name}`
          );
         
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
 
        const uploadedImageURLs = await Promise.all(uploadPromises);
        imageURLs = [...imageURLs, ...uploadedImageURLs]; // Merge new and old URLs
      }
 
      // Update Firestore (store URLs, not files)
      await updateDoc(invoiceRef, {
        ...newInvoice,
        images: imageURLs
      });
 
      // Update UI
      setInvoices(invoices.map(proposal =>
        proposal.id === selectedInvoice.id ? { ...proposal, images: imageURLs } : proposal
      ));
 
      setSelectedInvoice({ ...selectedInvoice, images: imageURLs });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating proposal:", error);
    }
  };
 

  const handleDeleteProposal = async () => {
    try {
      const invoiceRef = doc(db, "invoices", selectedInvoice.id);
await deleteDoc(invoiceRef);
      setInvoices(invoices.filter(invoice => invoice.id !== selectedInvoice.id));
      setSelectedInvoice(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting proposal:", error);
    }
  };

  const handleCancel = () => {
    setnewInvoice({
      logo: logoOptions[0]?.url || "",
      companyName: "",
      companyAddress: "",
      companyCityStateZip: "",
      siteName: "",
      management: "",
      managerName: "",
      address: "",
      cityStateZip: "",
      dropdownValues: {},
dropdownSelections: {},
dropdown: "",
chemicalUsed: "",
chemicalProducts: [],
pestTreated: "",
areasTreated: "",
windSpeed: "",
equipmentUsed: "",
weather: "",
temperature: "",
windDirection: "",
      timeIn: "",
      dateOfService: "",
      jobDescription: "",
      price: "",
      submittedDate: "",
      submittedBy: "",
      images: [],
      terms: "",
      invoiceNumber: "",
timeOut: "",
purchaseOrder: "None",
serviceDescription: "",
quantity: "1.00",
amount: "",
serviceRows: [
  {
    serviceDescription: "",
    quantity: "1.00",
    amount: "",
  },
],
technicianSignature: "",
customerSignature: "",
customerUnavailableToSign: false,
    });
    setIsEditing(false);
  };

  const parseJobDescription = (description) => {
    if (!description) return null;
    return description.split('\n').map((line, index) => {
      if (line.startsWith('((b))')) {
        return <p key={index} className="bold">{line.replace('((b))', '')}</p>;
      } else if (line.startsWith('((i))')) {
        return <p key={index} className="italic">{line.replace('((i))', '')}</p>;
      } else if (line.startsWith('((-))')) {
        return <li key={index} className="bullet">{line.replace('((-))', '')}</li>;
      } else {
        return <p key={index}>{line}</p>;
      }
    });
  };

  const handleEditClick = () => {
    if (selectedInvoice) {
      setnewInvoice({
        logo: logoOptions[0]?.url || "",
        companyName: selectedInvoice.companyName || "",
        companyAddress: selectedInvoice.companyAddress || "",
        companyCityStateZip: selectedInvoice.companyCityStateZip || "",
        siteName: selectedInvoice.siteName || "",
        management: selectedInvoice.management || "",
        managerName: selectedInvoice.managerName || "",
        address: selectedInvoice.address || "",
        cityStateZip: selectedInvoice.cityStateZip || "",
        dropdownValues: selectedInvoice.dropdownValues || {},
        dropdownSelections: selectedInvoice.dropdownSelections || {},
        dropdown: selectedInvoice.dropdown || "",
        chemicalUsed: selectedInvoice.chemicalUsed || "",
        chemicalProducts: selectedInvoice.chemicalProducts || [],
        pestTreated: selectedInvoice.pestTreated || "",
        areasTreated: selectedInvoice.areasTreated || "",
        windSpeed: selectedInvoice.windSpeed || "",
        equipmentUsed: selectedInvoice.equipmentUsed || "",
        weather: selectedInvoice.weather || "",
        temperature: selectedInvoice.temperature || "",
        windDirection: selectedInvoice.windDirection || "",
        timeIn: selectedInvoice.timeIn || "",
        dateOfService: selectedInvoice.dateOfService || "",
        jobDescription: selectedInvoice.jobDescription || "",
        price: selectedInvoice.price || "",
        submittedDate: selectedInvoice.submittedDate || "",
        submittedBy: selectedInvoice.submittedBy || "",
        images: selectedInvoice.images || [],
        terms: selectedInvoice.terms || "",
        invoiceNumber: selectedInvoice?.invoiceNumber || "",
timeOut: selectedInvoice?.timeOut || "",
purchaseOrder: selectedInvoice?.purchaseOrder || "None",
serviceDescription: selectedInvoice?.serviceDescription || "",
quantity: selectedInvoice?.quantity || "1.00",
amount: selectedInvoice?.amount || "",
serviceRows: getServiceRows(selectedInvoice),
technicianSignature: selectedInvoice?.technicianSignature || "",
customerSignature: selectedInvoice?.customerSignature || "",
customerUnavailableToSign: selectedInvoice?.customerUnavailableToSign || false,
      });
      setIsEditing(true);
    }
  };

  // Put this above your component or inside it before return
  const openPrintPreview = () => {
    const printable = document.getElementById("printableArea");
    if (!printable) return;
 
    // Create hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
 
    const doc = iframe.contentWindow || iframe.contentDocument;
    const iDoc = doc.document || doc;
 
    const content = printable.outerHTML;
    const printCss = `
  <style>
    /* Paper */
    @page {
      size: Letter;           /* change to A4 if you prefer */
      margin: 0.5in;
    }
    html, body {
      margin: 0;
      padding: 0;
      height: 100%;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color: #111;
      font: 12pt/1.35 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    }

    /* Hide app controls in print */
    @media print { .no-print { display: none !important; } }

    /* Make the layout single-column in print (so it can break naturally) */
    @media print {
      .row-container {
        display: block !important;        /* kill flex for print */
      }
      .row-container > * {
        width: 100% !important;           /* each card full-width in order */
      }
    }

    @media print {
  .companyinfo-container,
  .property-container {
    box-sizing: border-box !important;
    width: 100% !important;
    margin: 0 0 12px 0 !important;

    /* double-up border protection so the right edge never drops in print */
    border: 1px solid #000 !important;
    border-right: 1px solid #000 !important;
    background: #fff !important;
    background-clip: padding-box !important;

    /* safety outline (some Chrome print engines drop the last border pixel) */
    outline: 0.5pt solid #000 !important;
    outline-offset: 0 !important;
  }

  /* Total paragraph: respect newlines from textarea */
  .total-text {
    white-space: pre-line !important;  /* \n => line breaks */
    line-height: 1.35;
    font-size: 11pt;
  }
}

    /* Logo smaller */
    .companylogo-container { text-align: center; margin: 0 0 12px 0; }
    .companylogo-container img.resizable-logo {
      width: 120px !important;
      max-width: 120px !important;
      height: auto !important;
      display: inline-block;
    }

    /* Outer page frame */
    .page-border {
      border: 1px solid #333 !important;
      border-radius: 6px;
      padding: 16px;
      box-shadow: none !important;
      background: #fff !important;
    }

    /* Section cards — allow breaking through them */
    .companyinfo-container,
    .property-container,
    .scope-container,
    .description-container,
    .total-container,
    .image-container,
    .terms-container,
 

      /* ✨ Let sections split across pages if they are tall */
      break-inside: auto !important;
      page-break-inside: auto !important;
      overflow: visible !important;
    }

    /* Text tweaks */
    .companyinfo-container p,
    .property-container p,
    .scope-container p,
    .description-container p,
    .total-container p,
    .terms-container p,

    /* Bullets/formatting */
    .job-bullet { display: block; margin-left: 10px; }
    .job-bold   { font-weight: 700; }
    .job-italic { font-style: italic; }

    /* Images: grid that can flow to the next page naturally */
    .image-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      justify-content: center;
    }
    .image-wrapper {
      border: 1px solid #bbb;
      border-radius: 4px;
      background: #fafafa;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      width: 100%;
      aspect-ratio: 1 / 1; /* square cells; no fixed px height so they can flow */
      /* Each image "tile" stays intact, but the grid continues on next page */
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .proposal-image { width: 100%; height: 100%; object-fit: contain; }

    .sir-page {
  background: #fff;
  color: #111;
  font-size: 11pt;
  border: 1px solid #000;
  padding: 14px;
}

.sir-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.sir-logo {
  max-width: 170px;
  height: auto;
}

.sir-header-right {
  text-align: right;
  flex: 1;
}

.sir-header-right h1 {
  margin: 0 0 8px 0;
  font-size: 20pt;
}

.sir-header-meta {
  display: grid;
  gap: 4px;
  font-size: 11pt;
}

.sir-top-company {
  margin-bottom: 12px;
}

.sir-time-signatures {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  align-items: end;
}

.sir-time-box,
.sir-sign-box,
.sir-panel,
.sir-section,
.sir-service-table,
.sir-totals {
  border: 1px solid #000;
  background: #fff;
}

.sir-time-box {
  padding: 10px;
}

.sir-sign-box {
  padding: 10px;
  min-height: 90px;
}

.sir-sign-line {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #000;
  margin-bottom: 6px;
}

.sir-sign-label {
  text-align: center;
  font-size: 10pt;
}

.sir-signature-img {
  max-width: 100%;
  max-height: 42px;
  object-fit: contain;
}

.sir-location-billto-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.sir-panel {
  padding: 10px;
}

.sir-panel-title,
.sir-section-title {
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.sir-service-table {
  display: grid;
  grid-template-columns: 1.1fr 1fr 2fr 0.8fr 1fr;
  margin-bottom: 12px;
}

.sir-service-table > div {
  padding: 8px;
  border-right: 1px solid #000;
  border-bottom: 1px solid #000;
}

.sir-service-table > div:nth-child(5n) {
  border-right: none;
}

.sir-service-head {
  font-weight: 700;
  background: #f3f4f6;
}

.sir-totals {
  padding: 8px 10px;
  margin-bottom: 12px;
  display: grid;
  gap: 4px;
}

.sir-section {
  padding: 10px;
  margin-bottom: 12px;
}

.sir-section-body {
  white-space: pre-line;
  line-height: 1.35;
}

.sir-products-grid {
  display: grid;
  grid-template-columns: 1.2fr 2fr;
  gap: 6px 12px;
}

@media print {
  .sir-time-signatures,
  .sir-location-billto-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

    /* Signature row is small; allow it to break if needed */
    .signature-row p { font-size: 10.5pt; margin: 2px 0; }

    /* Utility: force a page break if you insert <div class="page-break"></div> */
    .page-break { break-before: page; page-break-before: always; }
  </style>
`;

 
    iDoc.open();
    iDoc.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Proposal</title>
          ${printCss}
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    iDoc.close();
 
    const doPrint = () => {
      try { (iframe.contentWindow || iframe).focus(); } catch {}
      try { (iframe.contentWindow || iframe).print(); } catch {}
      // Clean up after a bit
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch {}
      }, 1000);
    };
 
    if ((iframe.contentDocument || iframe).readyState === "complete") {
      setTimeout(doPrint, 150);
    } else {
      iframe.onload = () => setTimeout(doPrint, 150);
    }
  };
 


  const handlePrint = async () => {
    if (!selectedInvoice) return;
 
    // 1) Open the print preview immediately (keeps user gesture intact)
    openPrintPreview();
 
    // 2) Then do your PDF creation + upload in the background of this handler
    try {
      const now = new Date();
      const timestamp = now.toISOString();
      const formattedTimestamp = now.toISOString().replace(/[-T:]/g, "").split(".")[0];
      const invoicePDFname = `INVOICE - ${selectedInvoice.siteName} ${formattedTimestamp}.pdf`;
 
      // Create a simple PDF (or call your generatePDF(proposal) if preferred)
      const printable = document.getElementById("printableArea");
      const canvas = await html2canvas(printable, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
     
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "pt", "letter");
     
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
     
      const margin = 24;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
     
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
     
      let heightLeft = imgHeight;
      let position = margin;
     
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= usableHeight;
     
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }
     
      const pdfBlob = pdf.output("blob");
 
      const storage = getStorage();
      const pdfRef = ref(storage, `invoices/${selectedInvoice.id}/pdfs/${invoicePDFname}`);
 
      await uploadBytes(pdfRef, pdfBlob);
      const downloadURL = await getDownloadURL(pdfRef);
      setPdfDownloadUrl(downloadURL);
 
      // Record in subcollection
      const proposalPdfsRef = collection(db, `invoices/${selectedInvoice.id}/pdfs/`);
      await addDoc(proposalPdfsRef, {
        invoicePDF: downloadURL,
        invoicePDFname,
        timestamp,
        dateTimeString: now.toLocaleString(),
      });      
 
      // Optional: auto-open the saved PDF in a new tab so they can print the real PDF too
      // window.open(downloadURL, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error uploading PDF:", err);
      // Optional non-blocking UI feedback here
    }
  };
 
 
const getImageData = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const generatePDF = async (proposal, fontSize = 12, termsFontSize = 12, termsFontStyle = 'normal') => {
    const doc = new jsPDF();
    doc.setFontSize(fontSize);
   
    // Add border
    doc.rect(10, 10, 190, 277);

    // Logo container
    doc.setDrawColor(0);
    doc.setFillColor(255, 255, 255);
    doc.rect(15, 20, 180, 30, 'FD');
    doc.text("Logo:", 20, 30);
    if (proposal.logo) {
        const logoImage = await getImageData(proposal.logo);
        doc.addImage(logoImage, 'JPEG', 20, 35, 50, 15);
    }

    let yOffset = 60;

    // Company container
    doc.setFillColor(255, 255, 255);
    const companyHeight = 10 + (proposal.companyName ? 10 : 0) + (proposal.companyAddress ? 10 : 0) + (proposal.companyCityStateZip ? 10 : 0);
    doc.rect(15, yOffset, 180, companyHeight, 'FD');
    doc.text("Company:", 20, yOffset + 10);
    if (proposal.companyName) doc.text(proposal.companyName, 105, yOffset + 20, { maxWidth: 170, align: 'center' });
    if (proposal.companyAddress) doc.text(proposal.companyAddress, 105, yOffset + 30, { maxWidth: 170, align: 'center' });
    if (proposal.companyCityStateZip) doc.text(proposal.companyCityStateZip, 105, yOffset + 40, { maxWidth: 170, align: 'center' });
    yOffset += companyHeight + 10;

    // Site container
    const siteHeight = 10 + (proposal.siteName ? 10 : 0) + (proposal.management ? 10 : 0) + (proposal.managerName ? 10 : 0) + (proposal.address ? 10 : 0);
    doc.rect(15, yOffset, 180, siteHeight, 'FD');
    doc.text("Site:", 20, yOffset + 10);
    if (proposal.siteName) doc.text(`Site: ${proposal.siteName}`, 20, yOffset + 20, { maxWidth: 170 });
    if (proposal.management) doc.text(`Management: ${proposal.management}`, 20, yOffset + 30, { maxWidth: 170 });
    if (proposal.managerName) doc.text(`Manager: ${proposal.managerName}`, 20, yOffset + 40, { maxWidth: 170 });
    if (proposal.address) doc.text(`Address: ${proposal.address}`, 20, yOffset + 50, { maxWidth: 170 });
    yOffset += siteHeight + 10;

    // Scope container
    const scopeHeight = 20;
    doc.rect(15, yOffset, 180, scopeHeight, 'FD');
    doc.text("Scope:", 20, yOffset + 10);
    yOffset += scopeHeight + 10;

    // Job Description container
    const jobDescriptionLines = (proposal.jobDescription || '').split('\n');
    const jobDescriptionText = jobDescriptionLines.map(line => `• ${line}`).join('\n');
    const jobDescriptionHeight = jobDescriptionLines.length * 10 + 10; // Adjust height based on line count

    doc.rect(15, yOffset, 180, jobDescriptionHeight, 'FD'); // Single container
    doc.text("Job Description:", 20, yOffset + 10);
    doc.text(jobDescriptionText, 20, yOffset + 20, { maxWidth: 170, align: 'left' });

    yOffset += jobDescriptionHeight + 10; // Adjust for next section

    // Total Price container
    const totalPriceHeight = 20;
    doc.rect(15, yOffset, 180, totalPriceHeight, 'FD');
    doc.text("Total Price:", 20, yOffset + 10);
    doc.text(`$${proposal.price || ''}`, 20, yOffset + 20, { maxWidth: 170 });
    yOffset += totalPriceHeight + 10;

    // Options container
    if (proposal.options && proposal.options.length > 0) {
        doc.addPage();
        yOffset = 10;
        const optionsHeight = 20 + proposal.options.length * 10;
        doc.setFillColor(255, 255, 255);
        doc.rect(15, yOffset, 180, optionsHeight, 'FD');
        doc.text("Options:", 20, yOffset + 10);
        proposal.options.forEach((option, index) => {
            doc.text(`Option ${String.fromCharCode(65 + index)} Total: ${option}`, 20, yOffset + 20 + index * 10, { maxWidth: 170 });
        });
        yOffset += optionsHeight + 10;
    }

    // Additional Note container
    doc.addPage();
    yOffset = 10;
    const additionalNoteHeight = 30;
    doc.setFillColor(255, 255, 255);
    doc.rect(15, yOffset, 180, additionalNoteHeight, 'FD');
    doc.text("Additional Note:", 20, yOffset + 10);
    doc.text(proposal.additionalNote || '', 20, yOffset + 20, { maxWidth: 170 });
    yOffset += additionalNoteHeight + 10;

    // Image Upload container
    if (proposal.images && proposal.images.length > 0) {
        doc.addPage();
        yOffset = 10;
        const imagesHeight = 20 + Math.ceil(proposal.images.length / 3) * 110; // Adjust height based on image count
        doc.setFillColor(255, 255, 255);
        doc.rect(15, yOffset, 180, imagesHeight, 'FD');
        doc.text("Images:", 20, yOffset + 10);
        for (const [index, imageUrl] of proposal.images.entries()) {
            const imageData = await getImageData(imageUrl);
            const x = 20 + (index % 3) * 60; // Adjust x position for 3 images per row
            const y = yOffset + 20 + Math.floor(index / 3) * 100; // Adjust y position for each row
            doc.addImage(imageData, 'JPEG', x, y, 50, 50); // Adjust
        }
        yOffset += imagesHeight + 10;
    }

    // Add page break for terms
    doc.addPage();
    yOffset = 10;
    doc.rect(10, 10, 190, 277);
   
    // Terms & Conditions
    doc.setFontSize(termsFontSize);
    doc.setFont('helvetica', termsFontStyle);
    doc.text("Terms & Conditions:", 15, yOffset + 10);

    // Handle line breaks in terms
    const termsLines = (proposal.terms || '').split('\n');
    yOffset += 20;
    termsLines.forEach(line => {
        doc.text(line, 15, yOffset, { maxWidth: 180 });
        yOffset += 10;
    });
   
    // Save PDF
    const filename = `${proposal.siteName} ${proposal.scope}`.replace(/[/\\?%*:|"<>]/g, '') + '.pdf';
doc.save(filename);
};

const generateAndUploadPDF = async () => {
    const doc = new jsPDF();
    doc.text("This is a dynamically created PDF stored in Firebase!", 10, 10);
    const pdfBlob = doc.output("blob");

    const storage = getStorage();
    const pdfRef = ref(storage, `generated_pdfs/my_generated_pdf.pdf`);

    try {
      const snapshot = await uploadBytes(pdfRef, pdfBlob);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log("PDF uploaded successfully:", downloadURL);

      document.getElementById("pdfLink").href = downloadURL;
      document.getElementById("pdfLink").innerText = "Download Generated PDF";
      document.getElementById("pdfViewer").src = downloadURL;
    } catch (error) {
      console.error("Error uploading PDF:", error);
    }
  };

  const handleAddInvoiceClick = async () => {
    const ppd = companies.find(c => c.companyName === PPD_NAME);
    const nextInvoiceNumber = await getNextInspectionInvoiceNumber();
 
    setnewInvoice({
      logo: logoOptions[0]?.url || "",
      invoiceNumber: nextInvoiceNumber,
      companyName: PPD_NAME,
      companyAddress: ppd?.companyAddress || "",
      companyCityStateZip: ppd ? `${ppd.companyCity}, ${ppd.companyState} ${ppd.companyZip}` : "",
      siteName: "",
      management: "",
      managerName: "",
      address: "",
      cityStateZip: "",
      dropdownValues: {},
      dropdownSelections: {},
      dropdown: "",
      chemicalUsed: "",
chemicalProducts: [],
      pestTreated: "",
      areasTreated: "",
      windSpeed: "",
      equipmentUsed: "",
      weather: "",
      temperature: "",
      windDirection: "",
      timeIn: "",
      timeOut: "",
      dateOfService: "",
      purchaseOrder: "None",
      serviceDescription: "",
      quantity: "1.00",
      amount: "",
      serviceRows: [
        {
          serviceDescription: "",
          quantity: "1.00",
          amount: "",
        },
      ],
      jobDescription: "",
      price: "",
      submittedDate: "",
      submittedBy: "",
      images: [],
      terms: "NET 30",
      technicianSignature: "",
      customerSignature: "",
      customerUnavailableToSign: false,
    });
 
    setSelectedLogo(logoOptions[0]);
    setSelectedInvoice(null);
    setIsEditing(true);
  };


const handleCloseDetails = () => {
  setSelectedInvoice(null);
};

const handleDeleteProposalDirect = async (proposalId) => {
  try {
    if (!proposalId) return;

    const invoiceRef = doc(db, "invoices", proposalId);
    await deleteDoc(invoiceRef);

    setInvoices(prev => prev.filter(p => p.id !== proposalId));

    // If the deleted invoice was open, close it
    if (selectedInvoice?.id === proposalId) {
      setSelectedInvoice(null);
    }
  } catch (error) {
    console.error("Error deleting proposal:", error);
    alert("Failed to delete invoice.");
  }
};

const handleToggleInvoiceSent = async (invoice) => {
  try {
    const invoiceRef = doc(db, "invoices", invoice.id);

    await updateDoc(invoiceRef, {
      status: "Draft",
      sent: false,
    });

    // Remove from sent page immediately
    setInvoices((prev) => prev.filter((inv) => inv.id !== invoice.id));
  } catch (error) {
    console.error("Error updating invoice sent status:", error);
    alert("Failed to update invoice status.");
  }
};

const handleSortBySite = () => {
  const sorted = [...invoices].sort((a, b) => {
    const nameA = a.siteName?.toLowerCase() || "";
    const nameB = b.siteName?.toLowerCase() || "";

    if (sortOrder === "asc") {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  setInvoices(sorted);
  setSortOrder(sortOrder === "asc" ? "desc" : "asc");
};

const getServiceRows = (invoice = {}) => {
  if (Array.isArray(invoice.serviceRows) && invoice.serviceRows.length > 0) {
    return invoice.serviceRows;
  }

  return [
    {
      serviceDescription: invoice.serviceDescription || "",
      quantity: invoice.quantity || "1.00",
      amount: invoice.amount || "",
    },
  ];
};

const calculateServiceRowTotal = (row) => {
  const qty = parseFloat(row.quantity);
  const amt = parseFloat(row.amount);

  if (Number.isNaN(qty) || Number.isNaN(amt)) return 0;
  return qty * amt;
};

const calculateInvoiceTotal = (invoice = {}) => {
  return getServiceRows(invoice)
    .reduce((sum, row) => sum + calculateServiceRowTotal(row), 0)
    .toFixed(2);
};

const updateServiceRow = (index, field, value) => {
  setnewInvoice((prev) => {
    const rows = getServiceRows(prev).map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );

    return {
      ...prev,
      serviceRows: rows,
      serviceDescription: rows[0]?.serviceDescription || "",
      quantity: rows[0]?.quantity || "1.00",
      amount: rows[0]?.amount || "",
    };
  });
};

const addServiceRow = () => {
  setnewInvoice((prev) => {
    const rows = getServiceRows(prev);

    if (rows.length >= 10) {
      alert("You can add up to 10 services.");
      return prev;
    }

    return {
      ...prev,
      serviceRows: [
        ...rows,
        {
          serviceDescription: "",
          quantity: "1.00",
          amount: "",
        },
      ],
    };
  });
};

const removeServiceRow = (index) => {
  setnewInvoice((prev) => {
    const rows = getServiceRows(prev);

    if (rows.length === 1) return prev;

    const updatedRows = rows.filter((_, i) => i !== index);

    return {
      ...prev,
      serviceRows: updatedRows,
      serviceDescription: updatedRows[0]?.serviceDescription || "",
      quantity: updatedRows[0]?.quantity || "1.00",
      amount: updatedRows[0]?.amount || "",
    };
  });
};

return (
  <div className="invoices-container">

    <h2 style={{ margin: "0 0 12px 0", textAlign: "center" }}>
      Priority Pest Defense Invoices
    </h2>

    {isEditing && !selectedInvoice && (
  <div className="modal-overlay">
        <div className="modal">
          <h3>{selectedInvoice ? "Edit Invoice" : "Add Invoice"}</h3>

          <form className="form-container" onSubmit={handleSubmit}>
            <label>Logo:</label>
            <select
              name="logo"
              value={selectedLogo.name}
              onChange={handleLogoChange}
              required
            >
              {logoOptions.map((option) => (
                <option key={option.name} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>

            <label>Company Name:</label>
            <input
              type="text"
              name="companyName"
              value={newInvoice.companyName}
              readOnly
            />

            <label>Company Address:</label>
            <input
              type="text"
              name="companyAddress"
              value={newInvoice.companyAddress}
              onChange={handleInputChange}
              readOnly
            />

            <label>Company City, State, Zip:</label>
            <input
              type="text"
              name="companyCityStateZip"
              value={newInvoice.companyCityStateZip}
              onChange={handleInputChange}
              readOnly
            />

            <label>Site Name:</label>
            <select
              name="siteName"
              value={newInvoice.siteName}
              onChange={handleSiteNameChange}
              required
            >
              <option value="">Select Site Name</option>
              {sortedProperties.map((property) => (
                <option key={property.id} value={property.propertyName}>
                  {property.propertyName}
                </option>
              ))}
            </select>

            <label>Management:</label>
            <input
              type="text"
              name="management"
              value={newInvoice.management}
              onChange={handleInputChange}
              readOnly
            />

            <label>Manager Name:</label>
            <input
              type="text"
              name="managerName"
              value={newInvoice.managerName}
              onChange={handleInputChange}
              readOnly
            />

            <label>Address:</label>
            <select
              name="address"
              value={newInvoice.address}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Address</option>
              {properties.map((property) => (
                <option
                  key={property.id}
                  value={property.address || property.managementAddress}
                >
                  {property.address || property.managementAddress}
                </option>
              ))}
            </select>

            <label>City, State, Zip:</label>
            <select
              name="cityStateZip"
              value={newInvoice.cityStateZip}
              onChange={handleInputChange}
              required
            >
              <option value="">Select City, State, Zip</option>
              {properties.map((property) => (
                <option
                  key={property.id}
                  value={`${property.city || property.managementCity}, ${
                    property.state || property.managementState
                  } ${property.zip || property.managementZip}`}
                >
                  {`${property.city || property.managementCity}, ${
                    property.state || property.managementState
                  } ${property.zip || property.managementZip}`}
                </option>
              ))}
            </select>

            {invoiceDropdowns
  .filter((dropdown) =>
    [
      "dropdown",
      "chemicalUsed",
      "pestTreated",
      "areasTreated",
      "windSpeed",
      "equipmentUsed",
      "weather",
      "windDirection",
    ].includes(dropdown.fieldName)
  )
  .map((dropdown) => (
    <div key={dropdown.id}>
      <label>{dropdown.label}:</label>

      <CompactMultiSelect
        label={dropdown.label}
        options={dropdown.options || []}
        selectedValues={
          dropdown.fieldName === "chemicalUsed"
            ? (newInvoice.chemicalProducts || []).map((item) => item.chemical)
            : newInvoice.dropdownSelections?.[dropdown.fieldName] || []
        }
        onChange={(_, opt) =>
          handleDropdownToggle(dropdown.fieldName, opt, dropdown.label)
        }
        placeholder={`Select ${dropdown.label}`}
      />

      {dropdown.fieldName === "chemicalUsed" &&
        (newInvoice.chemicalProducts || []).length > 0 && (
          <div className="chemical-qty-list">
            {(newInvoice.chemicalProducts || []).map((item) => (
              <div key={item.chemical} className="chemical-qty-row">
                <span>{item.chemical} - {item.qty}</span>
                <button
                  type="button"
                  className="smallbl-button"
                  onClick={() => {
                    const updatedQty = promptForChemicalQty(
                      item.chemical,
                      item.qty
                    );
                    if (updatedQty === null) return;
                    upsertChemicalProduct(item.chemical, updatedQty);
                  }}
                >
                  Edit Qty
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  ))}
              <label>Temperature:</label>
<input
  type="text"
  name="temperature"
  value={newInvoice.temperature || ""}
  onChange={handleInputChange}
  placeholder="Enter temperature"
/>

            <label>Time In:</label>
            <input
              type="time"
              name="timeIn"
              value={newInvoice.timeIn || ""}
              onChange={handleInputChange}
            />

            <label>Date of Service:</label>
            <input
              type="date"
              name="dateOfService"
              value={newInvoice.dateOfService || ""}
              onChange={handleInputChange}
            />

<label>Time Out:</label>
<input
  type="time"
  name="timeOut"
  value={newInvoice.timeOut || ""}
  onChange={handleInputChange}
/>

<label>Purchase Order:</label>
<input
  type="text"
  name="purchaseOrder"
  value={newInvoice.purchaseOrder || ""}
  onChange={handleInputChange}
  placeholder="None"
/>

<label>Terms:</label>
<select
  name="terms"
  value={newInvoice.terms}
  onChange={handleInputChange}
>
  {staticTermsOptions.map((term) => (
    <option key={term} value={term}>
      {term}
    </option>
  ))}
</select>

<h4>Services</h4>

{getServiceRows(newInvoice).map((row, index) => (
  <div
    key={index}
    style={{
      border: "1px solid #ccc",
      padding: "10px",
      borderRadius: "8px",
      marginBottom: "10px",
    }}
  >
    <label>Service Description:</label>
    <CompactMultiSelect
      options={staticServiceDescriptions.map((service) => ({ title: service }))}
      selectedValues={
        row.serviceDescription
          ? row.serviceDescription.split("\n").filter(Boolean)
          : []
      }
      onChange={(updatedSelections) =>
        updateServiceRow(index, "serviceDescription", updatedSelections.join("\n"))
      }
      placeholder="Select service descriptions"
    />

    <label>Quantity:</label>
    <input
      type="text"
      value={row.quantity || "1.00"}
      onChange={(e) => updateServiceRow(index, "quantity", e.target.value)}
    />

    <label>Amount:</label>
    <input
      type="text"
      value={row.amount || ""}
      onChange={(e) => updateServiceRow(index, "amount", e.target.value)}
      placeholder="346.50"
    />

    <div>
      <strong>Line Total:</strong> ${calculateServiceRowTotal(row).toFixed(2)}
    </div>

    {getServiceRows(newInvoice).length > 1 && (
      <button
        type="button"
        className="red-button"
        onClick={() => removeServiceRow(index)}
      >
        Remove Service
      </button>
    )}
  </div>
))}

<button
  type="button"
  className="blue-button"
  onClick={addServiceRow}
>
  + Add Service
</button>

<label>Total Due:</label>
<input
  type="text"
  value={calculateInvoiceTotal(newInvoice)}
  readOnly
/>

<SignaturePadField
  label="Technician Signature"
  value={newInvoice.technicianSignature}
  onChange={(sig) =>
    setnewInvoice((prev) => ({ ...prev, technicianSignature: sig }))
  }
/>

<SignaturePadField
  label="Customer Signature"
  value={newInvoice.customerSignature}
  onChange={(sig) =>
    setnewInvoice((prev) => ({ ...prev, customerSignature: sig }))
  }
/>

<label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
  <input
    type="checkbox"
    checked={!!newInvoice.customerUnavailableToSign}
    onChange={(e) =>
      setnewInvoice((prev) => ({
        ...prev,
        customerUnavailableToSign: e.target.checked,
      }))
    }
  />
  Customer is unavailable to sign
</label>

<label>
  Job Description: (Notes: Add ((b)) for bold lines, add ((i)) for
  italic lines. Bold and italic lines, and spaces have no bullets.)
</label>
            <textarea
              name="jobDescription"
              value={newInvoice.jobDescription}
              onChange={handleInputChange}
            />

            <label>Job Description Preview:</label>
            <div className="job-description-preview">
              {parseJobDescription(newInvoice.jobDescription)}
            </div>


            <label>Submitted Date:</label>
            <input
              type="date"
              name="submittedDate"
              value={newInvoice.submittedDate}
              onChange={handleInputChange}
            />

            <label>Submitted By:</label>
            <select
              name="submittedBy"
              value={newInvoice.submittedBy}
              onChange={handleInputChange}
            >
              <option value="">Select User</option>
              {users.map((user) => {
                const initials = `${(user.firstName || "").charAt(0)} ${(
                  user.lastName || ""
                ).charAt(0)}`.trim();
                return (
                  <option key={user.id} value={initials}>
                    {initials}
                  </option>
                );
              })}
            </select>

            <label>Image Display:</label>
            <input
              type="file"
              name="imageDisplay"
              onChange={handleFileChange}
              multiple
            />

            <div className="modal-buttons">
              <button type="submit" className="blue-button">
                Save
              </button>
              <button
                type="button"
                className="red-button"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th style={{ whiteSpace: "nowrap" }}>
              Site Name
              <button
                onClick={handleSortBySite}
                style={{
                  marginLeft: "6px",
                  padding: "2px 6px",
                  fontSize: "11px",
                  cursor: "pointer",
                }}
              >
                {sortOrder === "asc" ? "▲" : "▼"}
              </button>
            </th>
            <th>Submitted Date</th>
            <th>User</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {invoices.map((proposal) => (
            <tr
              key={proposal.id}
              style={{
                backgroundColor:
                  proposal.status === "Approved"
                    ? "#d4edda"
                    : proposal.status === "Sent"
                    ? "#f8d7da"
                    : "transparent",
              }}
            >
              <td>{proposal.siteName}</td>
              <td>{formatDateMMDDYYYY(proposal.submittedDate)}</td>
              <td>{formatInitials(proposal.submittedBy)}</td>
              <td>
              <button
  className="smalldetails-button"
  onClick={() => setSelectedInvoice(proposal)}
>
  Details
</button>

<button
  className="smallstatus-button sent"
  onClick={() => handleToggleInvoiceSent(proposal)}
>
  Sent
</button>

<button
  className="smalldelete-button"
  onClick={() => {
    if (window.confirm("Delete this invoice?")) {
      handleDeleteProposalDirect(proposal.id);
    }
  }}
>
  Delete
</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {selectedInvoice && (
  <div className="modal-overlay">
    <div className="modal full-size-modal">
        {isEditing ? (
          <div className="form-container shrink-form">
            <label>Company Name:</label>
            <input
              type="text"
              name="companyName"
              value={newInvoice.companyName}
              readOnly
            />

            <label>Company Address:</label>
            <input
              type="text"
              name="companyAddress"
              value={newInvoice.companyAddress}
              onChange={handleInputChange}
              readOnly
            />

            <label>Company City, State, Zip:</label>
            <input
              type="text"
              name="companyCityStateZip"
              value={newInvoice.companyCityStateZip}
              onChange={handleInputChange}
              readOnly
            />

            <label>Site Name:</label>
            <select
              name="siteName"
              value={newInvoice.siteName}
              onChange={handleSiteNameChange}
              required
            >
              <option value="">Select Site Name</option>
              {sortedProperties.map((property) => (
                <option key={property.id} value={property.propertyName}>
                  {property.propertyName}
                </option>
              ))}
            </select>

            <label>Management:</label>
            <input
              type="text"
              name="management"
              value={newInvoice.management}
              onChange={handleInputChange}
              readOnly
            />

            <label>Manager Name:</label>
            <input
              type="text"
              name="managerName"
              value={newInvoice.managerName}
              onChange={handleInputChange}
              readOnly
            />

            <label>Address:</label>
            <select
              name="address"
              value={newInvoice.address}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Address</option>
              {properties.map((property) => (
                <option
                  key={property.id}
                  value={property.address || property.managementAddress}
                >
                  {property.address || property.managementAddress}
                </option>
              ))}
            </select>

            <label>City, State, Zip:</label>
            <select
              name="cityStateZip"
              value={newInvoice.cityStateZip}
              onChange={handleInputChange}
              required
            >
              <option value="">Select City, State, Zip</option>
              {properties.map((property) => (
                <option
                  key={property.id}
                  value={`${property.city || property.managementCity}, ${
                    property.state || property.managementState
                  } ${property.zip || property.managementZip}`}
                >
                  {`${property.city || property.managementCity}, ${
                    property.state || property.managementState
                  } ${property.zip || property.managementZip}`}
                </option>
              ))}
            </select>

            {invoiceDropdowns
  .filter((dropdown) =>
    [
      "dropdown",
      "chemicalUsed",
      "pestTreated",
      "areasTreated",
      "windSpeed",
      "equipmentUsed",
      "weather",
      "windDirection",
    ].includes(dropdown.fieldName)
  )
  .map((dropdown) => (
    <div key={dropdown.id}>
      <label>{dropdown.label}:</label>

      <CompactMultiSelect
        label={dropdown.label}
        options={dropdown.options || []}
        selectedValues={
          dropdown.fieldName === "chemicalUsed"
            ? (newInvoice.chemicalProducts || []).map((item) => item.chemical)
            : newInvoice.dropdownSelections?.[dropdown.fieldName] || []
        }
        onChange={(_, opt) =>
          handleDropdownToggle(dropdown.fieldName, opt, dropdown.label)
        }
        placeholder={`Select ${dropdown.label}`}
      />

      {dropdown.fieldName === "chemicalUsed" &&
        (newInvoice.chemicalProducts || []).length > 0 && (
          <div className="chemical-qty-list">
            {(newInvoice.chemicalProducts || []).map((item) => (
              <div key={item.chemical} className="chemical-qty-row">
                <span>{item.chemical} - {item.qty}</span>
                <button
                  type="button"
                  className="smallbl-button"
                  onClick={() => {
                    const updatedQty = promptForChemicalQty(
                      item.chemical,
                      item.qty
                    );
                    if (updatedQty === null) return;
                    upsertChemicalProduct(item.chemical, updatedQty);
                  }}
                >
                  Edit Qty
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  ))}

<label>Temperature:</label>
<input
  type="text"
  name="temperature"
  value={newInvoice.temperature || ""}
  onChange={handleInputChange}
  placeholder="Enter temperature"
/>

            <label>Time In:</label>
            <input
              type="time"
              name="timeIn"
              value={newInvoice.timeIn || ""}
              onChange={handleInputChange}
            />

            <label>Date of Service:</label>
            <input
              type="date"
              name="dateOfService"
              value={newInvoice.dateOfService || ""}
              onChange={handleInputChange}
            />

<label>Time Out:</label>
<input
  type="time"
  name="timeOut"
  value={newInvoice.timeOut || ""}
  onChange={handleInputChange}
/>

<label>Purchase Order:</label>
<input
  type="text"
  name="purchaseOrder"
  value={newInvoice.purchaseOrder || ""}
  onChange={handleInputChange}
  placeholder="None"
/>

<label>Terms:</label>
<select
  name="terms"
  value={newInvoice.terms}
  onChange={handleInputChange}
>
  {staticTermsOptions.map((term) => (
    <option key={term} value={term}>
      {term}
    </option>
  ))}
</select>

<h4>Services</h4>

{getServiceRows(newInvoice).map((row, index) => (
  <div
    key={index}
    style={{
      border: "1px solid #ccc",
      padding: "10px",
      borderRadius: "8px",
      marginBottom: "10px",
    }}
  >
    <label>Service Description:</label>
    <CompactMultiSelect
      options={staticServiceDescriptions.map((service) => ({ title: service }))}
      selectedValues={
        row.serviceDescription
          ? row.serviceDescription.split("\n").filter(Boolean)
          : []
      }
      onChange={(updatedSelections) =>
        updateServiceRow(index, "serviceDescription", updatedSelections.join("\n"))
      }
      placeholder="Select service descriptions"
    />

    <label>Quantity:</label>
    <input
      type="text"
      value={row.quantity || "1.00"}
      onChange={(e) => updateServiceRow(index, "quantity", e.target.value)}
    />

    <label>Amount:</label>
    <input
      type="text"
      value={row.amount || ""}
      onChange={(e) => updateServiceRow(index, "amount", e.target.value)}
      placeholder="346.50"
    />

    <div>
      <strong>Line Total:</strong> ${calculateServiceRowTotal(row).toFixed(2)}
    </div>

    {getServiceRows(newInvoice).length > 1 && (
      <button
        type="button"
        className="red-button"
        onClick={() => removeServiceRow(index)}
      >
        Remove Service
      </button>
    )}
  </div>
))}

<button
  type="button"
  className="blue-button"
  onClick={addServiceRow}
>
  + Add Service
</button>

<label>Total Due:</label>
<input
  type="text"
  value={calculateInvoiceTotal(newInvoice)}
  readOnly
/>

            <label>Job Description:</label>
            <textarea
              name="jobDescription"
              value={newInvoice.jobDescription}
              onChange={handleInputChange}
              rows={6}
            />

            <label>Job Description Preview:</label>
            <div className="job-description-preview">
              {newInvoice.jobDescription.split("\n").map((line, index) => {
                if (line.trim() === "") {
                  return <br key={index} />;
                } else if (line.startsWith("((b))")) {
                  return (
                    <span key={index} className="job-bold">
                      <strong>{line.replace("((b))", "").trim()}</strong>
                      <br />
                    </span>
                  );
                } else if (line.startsWith("((i))")) {
                  return (
                    <span key={index} className="job-italic">
                      <i>{line.replace("((i))", "").trim()}</i>
                      <br />
                    </span>
                  );
                } else {
                  return (
                    <span key={index} className="job-bullet">
                      • {line} <br />
                    </span>
                  );
                }
              })}
            </div>

            <label>Price:</label>
<textarea
  name="price"
  value={newInvoice.price}
  onChange={handleInputChange}
  rows={3}
/>

<SignaturePadField
  label="Technician Signature"
  value={newInvoice.technicianSignature}
  onChange={(sig) =>
    setnewInvoice((prev) => ({ ...prev, technicianSignature: sig }))
  }
/>

<SignaturePadField
  label="Customer Signature"
  value={newInvoice.customerSignature}
  onChange={(sig) =>
    setnewInvoice((prev) => ({ ...prev, customerSignature: sig }))
  }
/>

<label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
  <input
    type="checkbox"
    checked={!!newInvoice.customerUnavailableToSign}
    onChange={(e) =>
      setnewInvoice((prev) => ({
        ...prev,
        customerUnavailableToSign: e.target.checked,
      }))
    }
  />
  Customer is unavailable to sign
</label>

<label>Submitted Date:</label>
            <input
              type="date"
              name="submittedDate"
              value={newInvoice.submittedDate}
              onChange={handleInputChange}
            />

            <label>Submitted By:</label>
            <select
              name="submittedBy"
              value={newInvoice.submittedBy}
              onChange={handleInputChange}
            >
              <option value="">Select User</option>
              {users.map((user) => {
                const initials = `${(user.firstName || "").charAt(0)} ${(
                  user.lastName || ""
                ).charAt(0)}`.trim();
                return (
                  <option key={user.id} value={initials}>
                    {initials}
                  </option>
                );
              })}
            </select>

            <label>Image Display:</label>
            <input
              type="file"
              name="imageDisplay"
              onChange={handleFileChange}
              multiple
            />

            <div className="modal-buttons">
              <button onClick={handleEditProposal} className="blue-button">
                Save
              </button>
              <button onClick={handleCancel} className="red-button">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
  id="printableArea"
  className="printable-content full-size-content details-preview-only"
>
          <div className="sir-page">
            <div className="sir-header">
              <div className="sir-header-left">
                <img
                  src={selectedInvoice.logo || logoOptions[0]?.url}
                  alt="Company Logo"
                  className="sir-logo"
                />
              </div>
       
              <div className="sir-header-right">
                <h1>Service Inspection Report</h1>
                <div className="sir-header-meta">
                  <div><strong>INVOICE #:</strong> {selectedInvoice.invoiceNumber || ""}</div>
                  <div><strong>WORK DATE:</strong> {formatDateMMDDYYYY(selectedInvoice.dateOfService)}</div>
                </div>
              </div>
            </div>
       
            <div className="sir-top-company">
              <div>
                <strong>{selectedInvoice.companyName}</strong><br />
                {selectedInvoice.companyAddress}<br />
                {selectedInvoice.companyCityStateZip}
              </div>
            </div>
       
            <div className="sir-time-signatures">
              <div className="sir-time-box">
                <div><strong>Time In:</strong> {selectedInvoice.timeIn || ""}</div>
                <div><strong>Time Out:</strong> {selectedInvoice.timeOut || ""}</div>
              </div>
       
              <div className="sir-sign-box">
                <div className="sir-sign-line">
                  {selectedInvoice.technicianSignature ? (
                    <img src={selectedInvoice.technicianSignature} alt="Technician Signature" className="sir-signature-img" />
                  ) : null}
                </div>
                <div className="sir-sign-label">Technician Signature</div>
              </div>
       
              <div className="sir-sign-box">
                <div className="sir-sign-line">
                  {selectedInvoice.customerUnavailableToSign ? (
                    <span>Customer is unavailable to sign</span>
                  ) : selectedInvoice.customerSignature ? (
                    <img src={selectedInvoice.customerSignature} alt="Customer Signature" className="sir-signature-img" />
                  ) : null}
                </div>
                <div className="sir-sign-label">Customer Signature</div>
              </div>
            </div>
       
            <div className="sir-location-billto-row">
              <div className="sir-panel">
                <div className="sir-panel-title">LOCATION</div>
                <div><strong>{selectedInvoice.siteName}</strong></div>
                <div>{selectedInvoice.address}</div>
                <div>{selectedInvoice.cityStateZip}</div>
              </div>
       
              <div className="sir-panel">
  <div className="sir-panel-title">BILL-TO</div>
  {selectedInvoice.management || selectedInvoice.managerName ? (
    <>
      <div><strong>{selectedInvoice.management || selectedInvoice.siteName}</strong></div>
      <div>{selectedInvoice.managerName || ""}</div>
    </>
  ) : (
    <>
      <div><strong>{selectedInvoice.siteName}</strong></div>
      <div>{selectedInvoice.address}</div>
      <div>{selectedInvoice.cityStateZip}</div>
    </>
  )}
</div>
            </div>
       
            <div className="sir-service-table">
  <div className="sir-service-head">Purchase Order</div>
  <div className="sir-service-head">Terms</div>
  <div className="sir-service-head">Service Description</div>
  <div className="sir-service-head">Quantity</div>
  <div className="sir-service-head">Amount</div>

  {getServiceRows(selectedInvoice).map((row, index) => (
    <React.Fragment key={index}>
      <div>{index === 0 ? selectedInvoice.purchaseOrder || "None" : ""}</div>
      <div>{index === 0 ? selectedInvoice.terms || "" : ""}</div>
      <div style={{ whiteSpace: "pre-line" }}>{row.serviceDescription || ""}</div>
      <div>{row.quantity || "1.00"}</div>
      <div>{row.amount || ""}</div>
    </React.Fragment>
  ))}
</div>
       
            <div className="sir-totals">
            <div><strong>Total Due:</strong> ${calculateInvoiceTotal(selectedInvoice)}</div>
<div><strong>Tax:</strong> 0.00</div>
<div><strong>Subtotal:</strong> ${calculateInvoiceTotal(selectedInvoice)}</div>
            </div>
       
            <div className="sir-section">
              <div className="sir-section-title">GENERAL COMMENTS / INSTRUCTIONS</div>
              <div className="sir-section-body">
                {selectedInvoice.jobDescription.split("\n").map((line, index) => {
                  if (line.trim() === "") return <br key={index} />;
                  if (line.startsWith("((b))")) {
                    return (
                      <div key={index}>
                        <strong>{line.replace("((b))", "").trim()}</strong>
                      </div>
                    );
                  }
                  if (line.startsWith("((i))")) {
                    return (
                      <div key={index}>
                        <i>{line.replace("((i))", "").trim()}</i>
                      </div>
                    );
                  }
                  return <div key={index}>{line}</div>;
                })}
              </div>
            </div>
       
            <div className="sir-section">
              <div className="sir-section-title">PRODUCTS APPLICATION SUMMARY</div>
              <div className="sir-products-grid">
       
                <div><strong>Products Applied</strong></div>
                <div style={{ whiteSpace: "pre-line" }}>{selectedInvoice.pestTreated || ""}</div>
       
                <div><strong>Target Pests</strong></div>
                <div>{selectedInvoice.pestTreated || ""}</div>
       
                <div><strong>Areas Treated</strong></div>
                <div style={{ whiteSpace: "pre-line" }}>{selectedInvoice.areasTreated || ""}</div>
              </div>
            </div>
       
            <div className="sir-section">
              <div className="sir-section-title">PRODUCTS APPLIED</div>
              <div className="sir-products-grid">
              <div><strong>Material</strong></div>
<div style={{ whiteSpace: "pre-line" }}>
  {selectedInvoice.chemicalUsed ||
    buildChemicalUsedDisplay(selectedInvoice.chemicalProducts || []) ||
    ""}
</div>
       
                <div><strong>Application Equipment</strong></div>
                <div style={{ whiteSpace: "pre-line" }}>{selectedInvoice.equipmentUsed || ""}</div>
       
                <div><strong>Weather</strong></div>
                <div style={{ whiteSpace: "pre-line" }}>{selectedInvoice.weather || ""}</div>
       
                <div><strong>Temperature</strong></div>
                <div>{selectedInvoice.temperature || ""}</div>
       
                <div><strong>Wind Speed</strong></div>
                <div style={{ whiteSpace: "pre-line" }}>{selectedInvoice.windSpeed || ""}</div>
       
                <div><strong>Wind Direction</strong></div>
                <div style={{ whiteSpace: "pre-line" }}>{selectedInvoice.windDirection || ""}</div>
       
                <div><strong>Time</strong></div>
                <div>{selectedInvoice.timeIn || ""}</div>
              </div>
            </div>
       
            {selectedInvoice.imageURLs && selectedInvoice.imageURLs.length > 0 && (
              <div className="sir-section">
                <div className="sir-section-title">ATTACHED IMAGES</div>
                <div className="image-grid">
                  {selectedInvoice.imageURLs.map((imageUrl, index) => (
                    <div key={index} className="image-wrapper">
                      <img
                        src={imageUrl}
                        alt={`Uploaded ${index + 1}`}
                        className="proposal-image"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
       
          <div className="no-print">
            <div className="modal-buttons">
              <button onClick={handlePrint} className="blue-button">
                Print/Download
              </button>
              <button onClick={() => setIsEditing(true)} className="blue-button">
                Edit
              </button>
              <button onClick={handleCloseDetails} className="green-button">
                Close
              </button>
       
              {pdfDownloadUrl && (
                <div style={{ marginTop: "10px" }}>
                  <a
                    href={pdfDownloadUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="blue-button"
                  >
                    Download PDF
                  </a>
                </div>
              )}
            </div>
       
            <a id="pdfLink" href="#" target="_blank" rel="noreferrer"></a>
            <iframe id="pdfViewer" width="100%" height="600px"></iframe>
          </div>
        </div>
        )}
      </div>
      </div>
    )}

    {showDeleteConfirm && (
      <div className="modal-overlay">
        <div className="modal">
          <h3>Are you sure you want to delete?</h3>
          <div className="modal-buttons">
            <button onClick={handleDeleteProposal} className="red-button">
              Yes
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="green-button"
            >
              No
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default PPDSentInvoices;